import { useMutation, useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { Card } from '@/shared/ui/Card';
import { useToast } from '@/shared/ui/Toast';
import { rolloutApi } from '../../api/rolloutApi';
import { agencyKeys, commandCenterKeys, rolloutKeys } from '../../queryKeys';
import styles from './AccountTypeCard.module.css';

/** The practice-record flag the rollout service reads: see PRACTICE_FEATURE_FLAGS.internalPractice. */
export const INTERNAL_PRACTICE_FLAG = 'internalPractice';

function errorMessage(err: unknown): string {
  const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response
    ?.data?.error?.message;
  return message ?? 'Could not update the account type — try again.';
}

/**
 * Internal (PracticeNova's own) or a client practice. Was the Type column on
 * Rollout & Flags; it belongs with the rest of the practice record. Un-marking
 * is refused by the backend while an internal-only agent is still switched on.
 */
export function AccountTypeCard({
  practiceId,
  featureFlags,
}: {
  practiceId: string;
  featureFlags: Record<string, boolean>;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const internal = featureFlags[INTERNAL_PRACTICE_FLAG] === true;

  const setInternal = useMutation({
    mutationFn: (next: boolean) => rolloutApi.setInternalFlag(practiceId, next),
    onSuccess: (_result, next) => {
      queryClient.invalidateQueries({ queryKey: agencyKeys.practice(practiceId) });
      queryClient.invalidateQueries({ queryKey: rolloutKeys.all });
      queryClient.invalidateQueries({ queryKey: commandCenterKeys.practiceAgents(practiceId) });
      toast.success(next ? 'Marked as an internal practice.' : 'Marked as a client practice.');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card
      title="Account type"
      subtitle="Practice-wide. Internal practices can run internal-only agents."
    >
      <div className={styles.segmented} role="radiogroup" aria-label="Account type">
        <button
          type="button"
          role="radio"
          aria-checked={!internal}
          className={clsx(styles.segment, !internal && styles.segmentActive)}
          disabled={setInternal.isPending}
          onClick={() => !internal || setInternal.mutate(false)}
        >
          Client
        </button>
        <button
          type="button"
          role="radio"
          aria-checked={internal}
          className={clsx(styles.segment, internal && styles.segmentActive)}
          disabled={setInternal.isPending}
          onClick={() => internal || setInternal.mutate(true)}
        >
          Internal
        </button>
      </div>
    </Card>
  );
}
